import { Migration, OHIF } from '@gleamer/types';

type AuthHeaderSource = Pick<OHIF.UserAuthenticationService, 'getAuthorizationHeader'>;

type MigrationCatchUpChange = Migration.MigrationCatchUpChange;
type MigrationCatchUpChangesQuery = Migration.MigrationCatchUpChangesQuery;
type MigrationChanges = Migration.MigrationChanges;
type MigrationCommand = Migration.MigrationCommand;
type MigrationError = Migration.MigrationError;
type MigrationErrorGroup = Migration.MigrationErrorGroup;
type MigrationKindCount = Migration.MigrationKindCount;
type MigrationErrorsQuery = Migration.MigrationErrorsQuery;
type MigrationPage<T> = Migration.MigrationPage<T>;
type MigrationRequested = Migration.MigrationRequested;
type MigrationRun = Migration.MigrationRun;
type MigrationRunDetails = Migration.MigrationRunDetails;
type MigrationRunsQuery = Migration.MigrationRunsQuery;
type MigrationSegment = Migration.MigrationSegment;
type MigrationSegmentsQuery = Migration.MigrationSegmentsQuery;

const BASE_URL = '/api/admin/migration/runs';

/** An answer of viewer-api other than 2xx, with what the server said about it when it said something. */
export class MigrationApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'MigrationApiError';
  }
}

/**
 * What viewer-api said about a refusal: a JSON body (Spring's ProblemDetail says `detail`, its default error body
 * `message`), or plain text — viewer-api answers a validation refusal (IllegalArgumentException) with its message
 * as text/plain.
 */
async function failure(response: Response): Promise<MigrationApiError> {
  let detail = '';
  try {
    const text = (await response.text()).trim();
    try {
      const body = JSON.parse(text);
      detail = body?.detail ?? body?.message ?? body?.error ?? '';
    } catch {
      detail = text;
    }
  } catch {
    // no body
  }
  return new MigrationApiError(
    response.status,
    detail ? `${response.status}: ${detail}` : `${response.status} : ${response.statusText}`
  );
}

function query(params: Record<string, string | number | boolean | string[] | undefined | null>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([name, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    (Array.isArray(value) ? value : [value]).forEach(v => search.append(name, String(v)));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
}

async function get<T>(auth: AuthHeaderSource, path: string): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    headers: new Headers(auth.getAuthorizationHeader()),
  });
  if (!response.ok) {
    throw await failure(response);
  }
  return response.json();
}

async function post(
  auth: AuthHeaderSource,
  path: string,
  body?: unknown
): Promise<MigrationRequested> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: new Headers({
      ...auth.getAuthorizationHeader(),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    }),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw await failure(response);
  }
  return response.json();
}

const id = (runId: number | string) => encodeURIComponent(String(runId));

export function fetchMigrationRuns(
  auth: AuthHeaderSource,
  { page = 0, size = 20, type, state }: MigrationRunsQuery = {}
): Promise<MigrationPage<MigrationRun>> {
  return get(auth, query({ page, size, type, state }));
}

export async function fetchMigrationGroups(auth: AuthHeaderSource): Promise<string[]> {
  return (await get<{ groups: string[] }>(auth, '/groups')).groups;
}

/** The g3d environments a run may copy from, as migration-service publishes them. */
export async function fetchMigrationSources(auth: AuthHeaderSource): Promise<Migration.MigrationSource[]> {
  return (await get<{ sources: Migration.MigrationSource[] }>(auth, '/sources')).sources;
}

export function fetchMigrationRun(
  auth: AuthHeaderSource,
  runId: number | string
): Promise<MigrationRunDetails> {
  return get(auth, `/${id(runId)}`);
}

export function fetchMigrationSegments(
  auth: AuthHeaderSource,
  runId: number | string,
  { page = 0, size = 50, group, states }: MigrationSegmentsQuery = {}
): Promise<MigrationPage<MigrationSegment>> {
  return get(auth, `/${id(runId)}/segments${query({ page, size, group, state: states })}`);
}

export function fetchMigrationErrors(
  auth: AuthHeaderSource,
  runId: number | string,
  { page = 0, size = 50, kind, reason, acknowledged }: MigrationErrorsQuery = {}
): Promise<MigrationPage<MigrationError>> {
  return get(auth, `/${id(runId)}/errors${query({ page, size, kind, reason, acknowledged })}`);
}

/** A run's errors counted by kind and reason: the groups the page shows before any row. */
export function fetchMigrationErrorGroups(
  auth: AuthHeaderSource,
  runId: number | string
): Promise<MigrationErrorGroup[]> {
  return get(auth, `/${id(runId)}/errors/groups`);
}

/** A whole run's kinds counted side by side: expected, read, written, skipped, errors, in PostgreSQL, and whether they agree. */
export function fetchMigrationCounts(
  auth: AuthHeaderSource,
  runId: number | string
): Promise<MigrationKindCount[]> {
  return get(auth, `/${id(runId)}/counts`);
}

/** The changes a run's catch-up recorded, latest first: some statuses (any of them), one kind, or all. */
export function fetchMigrationCatchUpChanges(
  auth: AuthHeaderSource,
  runId: number | string,
  { page = 0, size = 50, statuses, kind }: MigrationCatchUpChangesQuery = {}
): Promise<MigrationPage<MigrationCatchUpChange>> {
  return get(auth, `/${id(runId)}/catch-up/changes${query({ status: statuses, kind, page, size })}`);
}

/** The body of an acknowledge or a rerun: ids, or a group's criteria less what was left out of it. */
function selection(errors: Migration.MigrationErrorSelection) {
  return 'errorIds' in errors
    ? { errorIds: errors.errorIds }
    : {
        kind: errors.kind ?? null,
        reason: errors.reason ?? null,
        excludedIds: errors.excludedIds ?? [],
      };
}

/** Whether the run moved since the mark `since` (none yet: `undefined`): the mark now, and the run when it differs. */
export function fetchMigrationChanges(
  auth: AuthHeaderSource,
  runId: number | string,
  since: string | undefined
): Promise<MigrationChanges> {
  return get(auth, `/${id(runId)}/changes${query({ since })}`);
}

/**
 * Sends a command to migration-service, through viewer-api and the outbox: accepted (`202`) is not done —
 * the run's next version shows its effect, or migration-service's log says why it ignored it.
 */
export function sendMigrationCommand(
  auth: AuthHeaderSource,
  command: MigrationCommand
): Promise<MigrationRequested> {
  switch (command.type) {
    case 'START':
      return post(auth, '', {
        type: command.runType,
        groups: command.groups ?? [],
        source: command.source,
        bigqueryExcluded: command.bigqueryExcluded ?? [],
      });
    case 'PAUSE':
    case 'RESUME':
    case 'STOP':
      return post(auth, `/${id(command.runId)}/${command.type.toLowerCase()}`);
    case 'STOP_CATCH_UP':
      return post(auth, `/${id(command.runId)}/catch-up/stop`);
    case 'RETRY':
      return post(auth, `/${id(command.runId)}/retry${query({ segmentId: command.segmentId })}`);
    case 'REPLAY':
      return post(auth, `/${id(command.runId)}/replay`, {
        group: command.group ?? null,
        segmentId: command.segmentId ?? null,
      });
    case 'THROTTLE':
      return post(auth, `/${id(command.runId)}/throttle`, { maxWorkers: command.maxWorkers });
    case 'ACKNOWLEDGE':
    case 'RERUN':
      return post(
        auth,
        `/${id(command.runId)}/errors/${command.type.toLowerCase()}`,
        selection(command.errors)
      );
  }
}
